"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, type ReactNode } from "react";

const SplitTransition = dynamic(
  () => import("./SplitTransition").then((m) => ({ default: m.SplitTransition })),
  { ssr: false },
);

/** Defer split-transition JS until idle or first interaction — lowers TBT on load. */
export function DeferredSplitTransition({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const mount = () => setReady(true);
    if (typeof requestIdleCallback === "function") {
      const id = requestIdleCallback(mount, { timeout: 2200 });
      return () => cancelIdleCallback(id);
    }
    const id = window.setTimeout(mount, 150);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (ready) return;
    const wake = () => setReady(true);
    document.addEventListener("pointerdown", wake, { once: true, capture: true });
    document.addEventListener("keydown", wake, { once: true, capture: true });
    return () => {
      document.removeEventListener("pointerdown", wake, { capture: true });
      document.removeEventListener("keydown", wake, { capture: true });
    };
  }, [ready]);

  if (!ready) return <>{children}</>;
  return <SplitTransition>{children}</SplitTransition>;
}
